import { useCallback, useEffect, useRef, useState } from 'react'
import * as ns from '@/services/newsletterSubscribersService'
import { useCurrentStore } from '@/context/CurrentStoreContext'
import { Card, CardHeader } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { SkeletonListPage } from '@/components/ui/Skeleton'
import { getUserFacingErrorMessage } from '@/utils/userFacingError'

const DEFAULT_LIMIT = 25

type StatusFilter = '' | 'subscribed' | 'unsubscribed'

function formatWhen(iso: string) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  } catch {
    return iso
  }
}

function statusBadge(status: string) {
  const subscribed = status === 'subscribed'
  return (
    <span
      className={
        subscribed
          ? 'inline-flex rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-400'
          : 'inline-flex rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground'
      }
    >
      {subscribed ? 'Subscribed' : 'Unsubscribed'}
    </span>
  )
}

export default function NewsletterSubscribersPage() {
  const { currentStoreId: storeId } = useCurrentStore()
  const [subscribedCount, setSubscribedCount] = useState(0)
  const [items, setItems] = useState<ns.NewsletterSubscriberRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [limit] = useState(DEFAULT_LIMIT)
  const [searchInput, setSearchInput] = useState('')
  const [q, setQ] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('')
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const skipQResetRef = useRef(true)

  useEffect(() => {
    const t = window.setTimeout(() => setQ(searchInput.trim()), 400)
    return () => window.clearTimeout(t)
  }, [searchInput])

  useEffect(() => {
    if (skipQResetRef.current) {
      skipQResetRef.current = false
      return
    }
    setPage(1)
  }, [q, statusFilter])

  useEffect(() => {
    setPage(1)
  }, [storeId])

  const load = useCallback(async () => {
    if (storeId == null) return
    setLoading(true)
    setError(null)
    try {
      const [stats, data] = await Promise.all([
        ns.fetchNewsletterStats(storeId),
        ns.fetchNewsletterSubscribers(storeId, {
          page,
          limit,
          q: q || undefined,
          status: statusFilter || undefined,
        }),
      ])
      setSubscribedCount(stats.subscribedCount)
      setItems(data.items)
      setTotal(data.total)
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Failed to load subscribers'))
    } finally {
      setLoading(false)
    }
  }, [storeId, page, limit, q, statusFilter])

  useEffect(() => {
    void load()
  }, [load])

  async function handleExport() {
    if (storeId == null) return
    setExporting(true)
    setError(null)
    try {
      const blob = await ns.exportNewsletterSubscribersCsv(storeId, {
        q: q || undefined,
        status: statusFilter || undefined,
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'newsletter-subscribers.csv'
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Export failed'))
    } finally {
      setExporting(false)
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / limit) || 1)
  const showingFrom = total === 0 ? 0 : (page - 1) * limit + (items.length === 0 ? 0 : 1)
  const showingTo = total === 0 ? 0 : (page - 1) * limit + items.length

  return (
    <Card>
      <CardHeader
        title="Newsletter"
        description="Storefront newsletter subscribers from your footer and signup popup."
        action={
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={exporting || !storeId || loading}
            onClick={() => void handleExport()}
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <p className="text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">{subscribedCount}</span> active subscribers
        </p>
        <Button type="button" variant="secondary" size="sm" onClick={() => void load()} disabled={loading || !storeId}>
          Refresh
        </Button>
      </div>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <div className="min-w-[200px] flex-1">
          <Input
            label="Search email"
            placeholder="name@example.com"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <label className="flex flex-col gap-1 text-sm text-muted-foreground">
          Status
          <select
            className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as StatusFilter)
              setPage(1)
            }}
          >
            <option value="">All</option>
            <option value="subscribed">Subscribed</option>
            <option value="unsubscribed">Unsubscribed</option>
          </select>
        </label>
      </div>

      {error ? <p className="mb-4 text-sm text-destructive">{error}</p> : null}

      {storeId == null ? (
        <p className="text-sm text-muted-foreground">No stores in this organisation.</p>
      ) : loading && items.length === 0 ? (
        <SkeletonListPage columns={5} rows={6} toolbarFields={2} />
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead className="border-b border-border bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Source</th>
                  <th className="px-4 py-3 font-medium">Subscribed</th>
                  <th className="px-4 py-3 font-medium">Unsubscribed</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                      No subscribers yet.
                    </td>
                  </tr>
                ) : (
                  items.map((row) => (
                    <tr key={row.id} className="border-b border-border/60 last:border-0">
                      <td className="px-4 py-3 font-medium text-foreground">{row.email}</td>
                      <td className="px-4 py-3">{statusBadge(row.status)}</td>
                      <td className="px-4 py-3 capitalize text-muted-foreground">{row.source}</td>
                      <td className="px-4 py-3 text-muted-foreground">{formatWhen(row.subscribedAt)}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {row.unsubscribedAt ? formatWhen(row.unsubscribedAt) : '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {showingFrom}–{showingTo} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </Card>
  )
}
